import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { KiFunktion, KiStatus, KiStatusGrund, KiStatusZustand } from '../models/ki.model';

interface ProjektStatus {
  zustand: WritableSignal<KiStatusZustand>;
  anfrage?: Subscription;
}

/** KI-Status je Projekt aus `GET …/ki/status`, einmal geladen und von allen KiButtons geteilt. */
@Injectable({ providedIn: 'root' })
export class KiStatusService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/projects`;
  private readonly projekte = new Map<string, ProjektStatus>();

  /** Ein Fehler bleibt nicht für die Sitzung stehen: der nächste Zugriff versucht es erneut. */
  status(projectId: string): Signal<KiStatusZustand> {
    const projekt = this.projekt(projectId);
    if (projekt.zustand().art === 'fehler' && projekt.anfrage?.closed !== false) {
      this.laden(projectId, projekt);
    }
    return projekt.zustand.asReadonly();
  }

  /** Nach Einstellungen, Verbindung oder einer Anfrage; bis zur Antwort bleibt der bisherige Stand sichtbar. */
  neuLaden(projectId: string): void {
    const projekt = this.projekte.get(projectId);
    if (projekt) {
      this.laden(projectId, projekt);
    } else {
      this.projekt(projectId);
    }
  }

  /**
   * Sperre aus einer Anfrage (409/403) sofort zeigen, ohne auf `neuLaden` zu warten. Ein Status,
   * der noch lädt oder fehlt, bleibt unberührt: die nächste Antwort bringt ihn ohnehin.
   */
  sperren(projectId: string, funktion: KiFunktion, grund: KiStatusGrund | undefined): void {
    const zustand = this.projekte.get(projectId)?.zustand;
    const aktuell = zustand?.();
    if (!zustand || aktuell?.art !== 'geladen') return;
    const funktionen = { ...aktuell.status.funktionen, [funktion]: { aktiv: false, grund } };
    zustand.set({ art: 'geladen', status: { ...aktuell.status, funktionen } });
  }

  private projekt(projectId: string): ProjektStatus {
    let projekt = this.projekte.get(projectId);
    if (!projekt) {
      projekt = { zustand: signal<KiStatusZustand>({ art: 'laedt' }) };
      this.projekte.set(projectId, projekt);
      this.laden(projectId, projekt);
    }
    return projekt;
  }

  private laden(projectId: string, projekt: ProjektStatus): void {
    projekt.anfrage?.unsubscribe();
    projekt.anfrage = this.http.get<KiStatus>(`${this.base}/${encodeURIComponent(projectId)}/ki/status`).subscribe({
      next: (status) => projekt.zustand.set({ art: 'geladen', status }),
      error: () => projekt.zustand.set({ art: 'fehler' }),
    });
  }
}
